import { ContentItem } from "~/components/ContentItem";
import { ContentItemGrid } from "~/components/ContentItemGrid";
import {
  ContentItemWithImage,
  ContentItemWithImageProps,
} from "~/components/ContentItemWithImage";
import { useActiveBreakpoint } from "~/lib/client/useActiveBreakpoint";
import { heDecodeOrDefault } from "~/lib/shared/html-utils";

export type VerticalPageHeaderSectionProps = {
  displayPrimaryTag: boolean;
  displayMetadata: boolean;
  displayDate: boolean;
  primaryTitle: string;
  primaryContentItems: ContentItemWithImageProps[];
  secondaryTitle?: string | null;
  secondaryContentItems?: ContentItemWithImageProps[];
  /** The column item will display as a "hero", giving more emphasis on the content items */
  columnIsHero?: boolean;
};

export const VerticalPageHeaderSection: React.FC<
  VerticalPageHeaderSectionProps
> = ({
  displayMetadata,
  displayDate,
  displayPrimaryTag,
  primaryContentItems,
  primaryTitle,
  secondaryContentItems = [],
  secondaryTitle = null,
  columnIsHero = false,
}) => {
  const isLargeScreen = useActiveBreakpoint("LARGE");
  return (
    //  Center and Right columns
    <>
      {!secondaryContentItems.length ? (
        <ContentItemGrid title={primaryTitle}>
          {primaryContentItems.map((props, index) => {
            const kicker = displayPrimaryTag ? props.kicker : null;
            const footer = displayMetadata ? props.footer : null;
            if (index === 0) {
              return (
                <ContentItemWithImage
                  imagePosition="top"
                  {...props}
                  key={index}
                  kicker={kicker}
                  body=""
                  footer={footer}
                  displayDate={displayDate}
                  noImageOnShrink
                  asHero={columnIsHero}
                />
              );
            }

            return columnIsHero ? (
              <ContentItemWithImage
                {...props}
                imagePosition="left"
                key={index}
                kicker={kicker}
                body=""
                footer={footer}
                displayDate={displayDate}
              />
            ) : (
              <ContentItem
                {...props}
                key={index}
                kicker={kicker}
                body=""
                footer={footer}
                displayDate={displayDate}
              />
            );
          })}
        </ContentItemGrid>
      ) : (
        // Left Column Display
        <div className="flex flex-col gap-y-xl lg:flex-row lg:flex-wrap lg:gap-x-m">
          <div className="flex-auto">
            <ContentItemGrid title={primaryTitle}>
              {primaryContentItems.map((props, index) => {
                const kicker = displayPrimaryTag ? props.kicker : null;
                const footer = displayMetadata ? props.footer : null;
                if (index === 0) {
                  return (
                    <ContentItemWithImage
                      imagePosition={isLargeScreen ? "top" : "left"}
                      {...props}
                      key={index}
                      kicker={kicker}
                      body=""
                      footer={footer}
                      displayDate={displayDate}
                      asHero={columnIsHero}
                    />
                  );
                }
                return (
                  <ContentItem
                    {...props}
                    key={index}
                    kicker={kicker}
                    body=""
                    footer={footer}
                    displayDate={displayDate}
                  />
                );
              })}
            </ContentItemGrid>
          </div>
          <div className="flex-auto basis-full md:basis-2/5 lg:basis-full">
            <ContentItemGrid title={heDecodeOrDefault(secondaryTitle)}>
              {secondaryContentItems.map((props, index) => (
                <ContentItem
                  {...props}
                  key={index}
                  kicker={displayPrimaryTag ? props.kicker : null}
                  body=""
                  footer={displayMetadata ? props.footer : null}
                  displayDate={displayDate}
                />
              ))}
            </ContentItemGrid>
          </div>
        </div>
      )}
    </>
  );
};
